import { useState } from "react";
import { useCategoriesQuery, useCreateCategory, useUpdateCategory, useDeleteCategory } from "../../hooks/category";
import { getExpenses } from "../../api/expense";
import { getIncomes } from "../../api/income";
import { 
  Alert,
  Chip,
  Box, 
  Typography, 
  Container, 
  useTheme,
  Fade,
  Grid,
  Avatar,
  Skeleton,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { 
  InsightsOutlined,
  LoginOutlined, 
  RuleFolderOutlined,
} from "@mui/icons-material";
import CategoryListComponent from "../../components/category/CategoryListComponent";
import Category from "../../models/finance/Category";
import { CategoryType } from "../../models/finance/CategoryType";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { PageContainer } from "../../components/common/StyledComponents";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import PrimaryButton from "../../components/common/ui/PrimaryButton";
import { useError } from "../../error/useError";
import ErrorAlert from "../../components/common/ErrorAlert";

const CategoryPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();

  const { error, helpText, clearError, handleError } = useError();
  const [addExpenseError, setAddExpenseError] = useState<string | null>(null);
  const [addIncomeError, setAddIncomeError] = useState<string | null>(null);

  const { data: expenseCategories = [], isLoading: loadingExpense, refetch: refetchExpense } = useCategoriesQuery(CategoryType.Expense, true);
  const { data: incomeCategories = [], isLoading: loadingIncome, refetch: refetchIncome } = useCategoriesQuery(CategoryType.Income, true);
  const { mutate: createCategory } = useCreateCategory();
  const { mutate: updateCategory } = useUpdateCategory();
  const { mutate: deleteCategory } = useDeleteCategory();

  const loading = loadingExpense || loadingIncome;

  const totalExpenseCategories = expenseCategories.length;
  const totalIncomeCategories = incomeCategories.length;
  const activeExpenseCategories = expenseCategories.filter((cat) => cat.isActive).length;
  const activeIncomeCategories = incomeCategories.filter((cat) => cat.isActive).length;
  const totalCategories = totalExpenseCategories + totalIncomeCategories;
  const totalActiveCategories = activeExpenseCategories + activeIncomeCategories;
  const totalInactiveCategories = totalCategories - totalActiveCategories;
  
  const fetchAllCategories = () => {
    refetchExpense();
    refetchIncome();
  };

  const handleAddCategory = (title: string, type: CategoryType) => {
    createCategory(
      { title, type },
      {
        onSuccess: () => {
          // Clear inline errors on success
          if (type === CategoryType.Expense) setAddExpenseError(null); else setAddIncomeError(null);
        },
        onError: (error) => {
          console.error("Error adding category:", error);
          // Prefer inline field error for duplicate title or validation issues
          const message = error && (error as any).userMessage ? (error as any).userMessage : "Failed to add category";
          if (type === CategoryType.Expense) setAddExpenseError(message); else setAddIncomeError(message);
        }
      }
    );
  };

  const handleDeleteCategory = (
    category: Category,
    fallbackCategoryId?: string
  ) => {
    deleteCategory(
      { category, fallbackCategoryId },
      {
        onError: (error) => {
          console.error("Error deleting category:", error);
          handleError(error, "Failed to delete category");
        }
      }
    );
  };

  const handleUpdateCategory = (
    oldCategory: Category,
    newTitle?: string,
    newIsActive?: boolean
  ) => {
    updateCategory(
      { category: oldCategory, newTitle, newIsActive },
      {
        onError: (error) => {
          console.error("Error updating category:", error);
          handleError(error, "Failed to update category");
        }
      }
    );
  };

  const getFinanceItemsNumber = async (
    categoryType: CategoryType,
    categoryId: string
  ): Promise<number> => {
    try {
      if (categoryType === CategoryType.Expense) {
        const expenses = await getExpenses(categoryId);
        return expenses.length;
      } else {
        const incomes = await getIncomes(categoryId);
        return incomes.length;
      }
    } catch (error) {
      console.error("Error fetching finance items count:", error);
      return 0;
    }
  };

  // Guest view for non-logged-in users
  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
          <Fade in={true} timeout={700}>
            <Box>
              <SurfaceCard
                sx={{
                  p: { xs: 3, md: 5 },
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "18px",
                  border: `1px solid ${theme.palette.primary.main}35`,
                  background: `linear-gradient(165deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: "auto -30% -65% auto",
                    width: 420,
                    height: 420,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${theme.palette.primary.main}1f 0%, transparent 60%)`,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    mx: "auto",
                    mb: 2,
                    backgroundColor: `${theme.palette.primary.main}26`,
                    color: theme.palette.primary.light,
                  }}
                >
                  <RuleFolderOutlined />
                </Avatar>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Category Command Center
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, mx: "auto", mb: 3 }}>
                  Build a clean, scalable category system for both expenses and income so reporting stays accurate and budgeting stays effortless.
                </Typography>
                <PrimaryButton
                  startIcon={<LoginOutlined />}
                  onClick={() => navigate(LOGIN_ROUTE_PATH)}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  }}
                >
                  Sign In to Manage Categories
                </PrimaryButton>
              </SurfaceCard>
            </Box>
          </Fade>
        </Container>
      </PageContainer>
    );
  }

  // Loading state for logged-in users
  if (loading) {
    return (
      <PageContainer>
        <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 4, md: 5 } }}>
          <Fade in={true} timeout={600}>
            <Box>
              <Skeleton variant="rounded" height={170} sx={{ mb: 2.5, borderRadius: "18px" }} />
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                {[...Array(4)].map((_, idx) => (
                  <Grid item xs={12} sm={6} lg={3} key={idx}>
                    <Skeleton variant="rounded" height={108} sx={{ borderRadius: "16px" }} />
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rounded" height={520} sx={{ borderRadius: "18px" }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rounded" height={520} sx={{ borderRadius: "18px" }} />
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Container>
      </PageContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <ErrorAlert
            error={error}
            helpText={helpText}
            onClear={clearError}
            onRetry={fetchAllCategories}
            title="Category Error"
          />
        </Container>
      </PageContainer>
    );
  }

  // Logged-in user view
  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 4, md: 5 } }}>
        <Fade in={true} timeout={600}>
          <Box>
            <SurfaceCard
              sx={{
                mb: 3,
                p: { xs: 2.5, md: 4 },
                borderRadius: "18px",
                border: `1px solid ${theme.palette.primary.main}2f`,
                background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: "-160px auto auto -100px",
                  width: 320,
                  height: 320,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${theme.palette.primary.main}22 0%, transparent 66%)`,
                },
              }}
            >
              <Grid container spacing={2.5} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                    <Avatar sx={{ bgcolor: `${theme.palette.primary.main}24`, color: theme.palette.primary.light }}>
                      <InsightsOutlined />
                    </Avatar>
                    <Chip
                      label={`${totalCategories} categories configured`}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 700,
                        backgroundColor: `${theme.palette.primary.main}1a`,
                        color: theme.palette.primary.light,
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      mb: 0.8,
                    }}
                  >
                    Category Management
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
                    Maintain a clean category structure across expenses and income to keep transaction entry fast and reports reliable.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: `${theme.palette.primary.main}14`,
                      border: `1px solid ${theme.palette.primary.main}35`,
                      color: theme.palette.primary.light,
                      py: 0.8,
                    }}
                  >
                    Use active categories for day-to-day entry. Inactive categories stay preserved for historical reporting and can be reactivated anytime.
                  </Alert>
                </Grid>
              </Grid>
            </SurfaceCard>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <SurfaceCard sx={{ p: 2, borderRadius: "16px" }}>
                  <Typography variant="body2" color="text.secondary">Total Categories</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>{totalCategories}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {totalActiveCategories} active • {totalInactiveCategories} inactive
                  </Typography>
                </SurfaceCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SurfaceCard sx={{ p: 2, borderRadius: "16px" }}>
                  <Typography variant="body2" color="text.secondary">Expense Categories</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: theme.palette.error.light }}>
                    {totalExpenseCategories}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeExpenseCategories} active • {totalExpenseCategories - activeExpenseCategories} inactive
                  </Typography>
                </SurfaceCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SurfaceCard sx={{ p: 2, borderRadius: "16px" }}>
                  <Typography variant="body2" color="text.secondary">Income Categories</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: theme.palette.success.light }}>
                    {totalIncomeCategories}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeIncomeCategories} active • {totalIncomeCategories - activeIncomeCategories} inactive
                  </Typography>
                </SurfaceCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SurfaceCard sx={{ p: 2, borderRadius: "16px" }}>
                  <Typography variant="body2" color="text.secondary">Inactive Categories</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {totalInactiveCategories}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Hidden from new transactions
                  </Typography>
                </SurfaceCard>
              </Grid>
            </Grid>

            {/* Category Management Grid */}
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                <CategoryListComponent
                  categoryType={CategoryType.Expense}
                  categories={expenseCategories}
                  onAddCategory={handleAddCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onUpdateCategory={handleUpdateCategory}
                  getFinanceItemsNumber={getFinanceItemsNumber}
                  addError={addExpenseError}
                  onClearAddError={() => setAddExpenseError(null)}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <CategoryListComponent
                  categoryType={CategoryType.Income}
                  categories={incomeCategories}
                  onAddCategory={handleAddCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onUpdateCategory={handleUpdateCategory}
                  getFinanceItemsNumber={getFinanceItemsNumber}
                  addError={addIncomeError}
                  onClearAddError={() => setAddIncomeError(null)}
                />
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default CategoryPage;
